import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { forwardRef, useMemo, useCallback } from 'react';
import { useTable, useGlobalFilter, useFilters, useSortBy, useColumnOrder, useRowSelect } from 'react-table';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useSticky } from 'react-table-sticky';
import { styled } from '@mui/material/styles';
import MuiBox from '@mui/material/Box';
import MuiCircularProgress from '@mui/material/CircularProgress';
import { Box } from '@mui/system';
import _ from 'lodash';
import TopBar from './TopBar/TopBar.js';
import CustomScrollbar from './CustomScrollbar/CustomScrollbar.js';
import useGlobalStyles from '../../../theme/global.js';
import Table from './Table/Table.js';
import TableHeader from './Table/TableHeader.js';
import TableBody from './Table/TableBody.js';
import TableEmptyState from './Table/TableEmptyState.js';
import TableResizeHandler from './Table/TableResizeHandler.js';
import TableList from './Table/TableList.js';
import './Table/HeaderRow/TableHeaderRow.js';
import './Table/HeaderCell/TableHeaderCell.js';
import './Table/DataCell/TableDataCell.js';
import TableDataRow from './Table/DataRow/TableDataRow.js';
import useColumnSize from '../hooks/useColumnSize.js';
import useCheckboxSelection from '../hooks/useCheckboxSelection.js';
import useRowMenu from '../hooks/useRowMenu.js';
import { useControlledState } from '../hooks/useControlledState.js';
import useColumnDrag from '../hooks/useColumnDrag.js';
import useRowClick from '../hooks/useRowClick.js';
import useColumnResize from '../hooks/useColumnResize.js';
import useColumnFilters from '../hooks/useColumnFilters.js';
import useKeyboardRowNavigation from '../hooks/useKeyboardRowNavigation.js';
import useValueService from '../hooks/useValueService.js';
import useModifiedColumns from '../hooks/useModifiedColumns.js';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll.js';
import { useRowDragHelper } from './helpers/useRowDragHelper.js';
import { useTableDataHelper } from './helpers/useTableDataHelper.js';
import { useRowRenderHelper } from './helpers/useRowRenderHelper.js';
import { useVirtualListHelper } from './helpers/useVirtualListHelper.js';
import { useColumnWidthHelper } from './helpers/useColumnWidthHelper.js';
import defaultOptions from './defaultOptions.js';

const DataTableRoot = styled(MuiBox) `
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const Root = styled(MuiBox) `
  flex: 1;
  width: 100%;
  position: relative;
`;
const Loader = styled(MuiCircularProgress) `
  transform: translate(-50%, -50%);
  z-index: 10;
`;
const CenteredLoader = () => (jsx(Box, Object.assign({ sx: {
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'absolute',
        zIndex: 10,
    } }, { children: jsx(Loader, { "data-testid": "loader", size: 88, thickness: 1 }) })));
const DataTable = forwardRef(({ data, columns, options, rowComponent: Row = TableDataRow, loading = false, className, listComponent = TableList, listRef, topBarOptions, rowSkeleton, 'data-testid': testId, searchInputProps, infiniteScroll, }, ref) => {
    useGlobalStyles();
    const modifiedColumns = useModifiedColumns(columns);
    const dataTableOptions = useMemo(() => Object.assign({}, defaultOptions, options, { columns: modifiedColumns }), [options, modifiedColumns]);
    const { loadingType = 'skeleton', onRowDoubleClick, onRowClick, headerHeight = 38, emptyStateLabel, onRowsDrag, onRowSelectionChanged, onRowCheckboxChanged, onSelectAllCheckboxChanged, onStateChanged, initialState, canDropOnRow, isRowHidden, enableColumnDragging, disableRowFiltering, enableRowClickSelection, enableCheckboxSelection, enableRowDragging, disableSortRemove, disableSortBy, disableResizing, panelWidth, rtl, enableRowClickKeyboardNavigation, columnResizingShift, getRowMenu, denseRowContextMenu, rowCellPadding, headerCellPadding, defaultSortedColumn, onRowContextMenuOpen, onRowContextMenuClose, focusable, } = dataTableOptions;
    const skeletonLoading = useMemo(() => loading && loadingType === 'skeleton', [loading, loadingType]);
    const circularLoading = useMemo(() => loading && loadingType === 'circular', [loading, loadingType]);
    const modifiedInitialState = useMemo(() => {
        let sortBy = [];
        if (defaultSortedColumn) {
            sortBy = _.uniqBy([...((initialState === null || initialState === void 0 ? void 0 : initialState.sortBy) || []), defaultSortedColumn], 'id');
        }
        return Object.assign(Object.assign({}, initialState), { sortBy });
    }, [initialState, defaultSortedColumn]);
    const rowHeight = ((dataTableOptions === null || dataTableOptions === void 0 ? void 0 : dataTableOptions.rowHeight) || 58) + 4;
    let { tableData } = useTableDataHelper(skeletonLoading, data);
    const columnFilters = useColumnFilters(modifiedColumns);
    const { modifiedTableData, onItemsRendered } = useInfiniteScroll({
        infiniteScroll,
        tableData,
        isLoading: loading,
    });
    tableData = modifiedTableData;
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, state, selectRowRange, onColumnDrop, resizeTable, setGlobalFilter, setRowClicked, resetRowClicked, toggleAllRowsSelected, dispatch, valueService, visibleColumns, columnOrder, } = useTable(Object.assign({ columns: modifiedColumns, data: tableData, getRowId: (row) => row.id, initialState: modifiedInitialState, autoResetSelectedRows: false, autoResetSortBy: false, autoResetResize: false, autoResetGlobalFilter: options === null || options === void 0 ? void 0 : options.autoResetGlobalFilter, rtl,
        disableSortRemove,
        disableSortBy }, columnFilters), hooks => {
        hooks.dataTableOptions = dataTableOptions;
        hooks.useInstance.push(instance => {
            instance.dataTableOptions = dataTableOptions;
        });
        hooks.useInstanceBeforeDimensions.push(instance => {
            instance.dataTableOptions = dataTableOptions;
        });
    }, useGlobalFilter, useFilters, useSortBy, useColumnOrder, useRowSelect, useCheckboxSelection, useRowMenu, useColumnResize, useColumnSize, useColumnDrag, useRowClick, useValueService, useSticky);
    const { selectedRowIds, clickedRowId, columnSizing: { totalHeight, columnWidths, totalWidth }, } = state;
    useControlledState(state, modifiedInitialState, rows, skeletonLoading, selectRowRange, onRowSelectionChanged, onStateChanged);
    const { totalColumnWidth, totalStickyColumnSize } = useColumnWidthHelper(columnWidths, visibleColumns, totalWidth);
    const { ScrollingVirtualList } = useVirtualListHelper(listComponent);
    const { onRowsDragHandler } = useRowDragHelper(dispatch, onRowsDrag);
    const { onScroll } = useKeyboardRowNavigation({
        clickedRowId,
        enableRowClickSelection,
        rows,
        onRowClick,
        onRowDoubleClick,
        setRowClicked,
        resetRowClicked,
        totalHeight,
        rowHeight,
        enableRowClickKeyboardNavigation,
    });
    const onRowFocus = useCallback((row) => {
        if (!clickedRowId && focusable) {
            onRowClick === null || onRowClick === void 0 ? void 0 : onRowClick(row);
            setRowClicked === null || setRowClicked === void 0 ? void 0 : setRowClicked(row.id);
        }
    }, [clickedRowId, focusable, onRowClick, setRowClicked]);
    const rowOptions = {
        rows,
        prepareRow,
        rowHeight,
        rtl,
        Row,
        onRowClick,
        onRowDoubleClick,
        onRowFocus,
        onRowsDragHandler,
        canDropOnRow,
        isRowHidden,
        clickedRowId,
        selectedRowIds,
        rowSkeleton,
        enableRowClickSelection,
        enableRowDragging,
        enableCheckboxSelection,
        loading: skeletonLoading,
        valueService,
        disableSortBy,
        toggleAllRowsSelected,
        columnResizingShift,
        getRowMenu,
        denseRowContextMenu,
        rowCellPadding,
        onRowContextMenuOpen,
        onRowContextMenuClose,
        onRowCheckboxChanged,
        onSelectAllCheckboxChanged,
    };
    const { RenderRow } = useRowRenderHelper(rowOptions);
    const itemSize = useCallback(() => rowHeight, [rowHeight]);
    const showHorizontalScroll = useMemo(() => {
        if (Math.abs(totalColumnWidth - totalWidth) <= 4) {
            return false;
        }
        return totalColumnWidth > totalWidth;
    }, [totalColumnWidth, totalWidth]);
    return (jsxs(DataTableRoot, { children: [topBarOptions && (jsx(TopBar, { globalFilter: state.globalFilter, setGlobalFilter: setGlobalFilter, rtl: rtl, disabled: loading, topBarOptions: topBarOptions, searchInputProps: searchInputProps })), jsx(Root, Object.assign({ className: className, ref: ref, "data-testid": testId }, { children: jsx(AutoSizer, Object.assign({ onResize: ({ width, height }) => resizeTable && resizeTable({ width: width, height: height - headerHeight }) }, { children: ({ height, width }) => (jsxs(Fragment, { children: [jsx(TableResizeHandler, { height: height - headerHeight, width: width, onResize: resizeTable }), jsxs(Table, Object.assign({}, getTableProps(), { width: width, style: !rows.length ? { height: `${height}px` } : {} }, { children: [jsx(TableHeader, Object.assign({}, {
                                        headerGroups,
                                        rtl,
                                        headerHeight,
                                        width,
                                        onColumnDrop,
                                        onSelectAllCheckboxChanged,
                                        onRowCheckboxChanged,
                                        columnResizingShift,
                                        enableColumnDragging,
                                        disableRowFiltering,
                                        disableSortBy,
                                        disableResizing,
                                        disabled: rows.length === 0,
                                        headerCellPadding,
                                        columnOrder,
                                    })), rows.length || skeletonLoading ? (jsxs(Fragment, { children: [jsxs(TableBody, Object.assign({}, getTableBodyProps(), { panelWidth: panelWidth, width: width, rtl: rtl, className: "styled-scrollbar" }, { children: [circularLoading && jsx(CenteredLoader, {}), jsx(ScrollingVirtualList, Object.assign({ height: height - headerHeight, width: width, itemCount: skeletonLoading ? Math.floor(height / rowHeight) : rows.length, itemSize: itemSize, onItemsRendered: onItemsRendered, itemData: { width, height, itemSize: itemSize() }, onScroll: onScroll, overscanCount: 5, listRef: listRef }, { children: RenderRow }))] })), showHorizontalScroll && (jsx(CustomScrollbar, Object.assign({}, { rtl, width, totalStickyColumnSize, totalColumnWidth })))] })) : circularLoading ? (jsx(CenteredLoader, {})) : (jsx(TableEmptyState, Object.assign({ height: height }, { children: emptyStateLabel || 'No data to display' })))] }))] })) })) }))] }));
});
DataTable.displayName = 'DataTable';

export { DataTableRoot, Root, DataTable as default };
//# sourceMappingURL=DataTable.js.map
